import { Info, WarningCircle, XCircle } from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Permission, type Organization } from '@ruta/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/hooks/useQueries';
import { usePermission } from '@/hooks/usePermission';
import { useImportForkVisibility } from '@/hooks/useImportForkVisibility';
import { useSetupChecklist } from '@/hooks/useSetupChecklist';
import { useOrgFeatures } from '@/hooks/useOrgFeatures';

type RutaPayStatusValue = Organization['rutaPayStatus']['status'];

interface BannerConfig {
  tone: 'danger';
  icon: Icon;
  title: string;
  /** The one sentence that tells the owner where the request actually stands. */
  detail: string;
  /** Present only when the owner can do something about it. */
  actionText?: string;
}

/**
 * Statuses the owner needs to hear about — the ones where something is wrong or
 * wanted from them. `approved`/`enabled` are absent because payments work;
 * `pending`/`submitted` are absent because the application is simply in flight
 * and there is nothing to do. A banner with "no action needed" on it is noise.
 */
const BANNERS: Partial<Record<RutaPayStatusValue, BannerConfig>> = {
  not_connected: {
    tone: 'danger',
    icon: WarningCircle,
    title: 'Payment processing not set up',
    detail: 'Customers cannot pay you until your business is approved.',
    actionText: 'Set up payments',
  },
  requires_info: {
    tone: 'danger',
    icon: Info,
    title: 'Payment processing needs more information',
    detail: 'Your application is on hold until the missing details are provided.',
    actionText: 'Finish application',
  },
  rejected: {
    tone: 'danger',
    icon: XCircle,
    title: 'Payment processing was declined',
    detail: 'Update your business information and resubmit, or contact support.',
    actionText: 'Review application',
  },
};

/**
 * Where the owner's payment-processing request stands, on every page of the app —
 * except Live while setup is still in progress, where the "Finish setting up" card
 * already carries it, and never for a company not taking cards.
 *
 * Payment setup is requested during onboarding and then decided asynchronously by a webhook,
 * which means the answer arrives when the owner is not looking. Without this the first sign that
 * payments were never enabled is a failed charge, so the banner stays up until the request is
 * approved — it is not dismissible.
 */
export function ProvisioningBanner({ className }: { className?: string }) {
  const navigate = useNavigate();
  const canManageCompany = usePermission(Permission.COMPANY_MANAGE);
  const { data: orgData, isLoading } = useOrganization({ enabled: canManageCompany });
  const { showPayments } = useImportForkVisibility();
  const { skipJustifiRegistration } = useOrgFeatures();
  const { tracksActive: checklistShowing } = useSetupChecklist();
  const { pathname } = useLocation();

  const organization = orgData?.organization as Organization | undefined;
  const status = organization?.rutaPayStatus?.status;

  // Only the owner/admin can act on this, and only once we know the real status.
  // Not taking cards here — the payment-setup banner never comes up (RUT-3503).
  // Skip-justifi flag — same: they may use Ruta without registering; do not nag.
  // On Live, while setup is still in progress, payments are already a track on the
  // "Finish setting up" card in the activity feed — so there, and only there, this
  // banner stands down rather than saying the same thing twice.
  const checklistOnThisPage = checklistShowing && pathname === '/assistant';
  if (
    !canManageCompany ||
    isLoading ||
    !status ||
    !showPayments ||
    skipJustifiRegistration ||
    checklistOnThisPage
  ) {
    return null;
  }

  const config = BANNERS[status];
  if (!config) return null;

  const IconComponent = config.icon;

  return (
    <div
      className={cn(
        'flex items-center gap-3 border-b border-white/10 bg-white/[0.06] px-4 py-2.5',
        className,
      )}
    >
      <IconComponent className="h-4 w-4 shrink-0 text-destructive" weight="fill" />
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium text-white">{config.title}</span>
        <span className="ml-2 hidden text-xs text-white/60 sm:inline">{config.detail}</span>
      </div>
      {config.actionText && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/settings?tab=billing')}
          className="h-7 shrink-0 border border-white/20 px-3 text-white hover:bg-white/10 hover:text-white"
        >
          {config.actionText}
        </Button>
      )}
    </div>
  );
}

export default ProvisioningBanner;
